import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AcceptRateComponent } from '@freelancer/components/accept-rate';
import { DirectoryItemComponent } from '@freelancer/components/directory-item';
import { EarningsComponent } from '@freelancer/components/earnings';
import { ReviewCountComponent } from '@freelancer/components/review-count';
import { UpgradeTagComponent } from '@freelancer/components/upgrade-tag';
import { UsernameComponent } from '@freelancer/components/username';
import { VideoRecordInputComponent } from '@freelancer/components/video-record-input';
import { WorkedTimeComponent } from '@freelancer/components/worked-time';
import { FeatureFlagsModule } from '@freelancer/feature-flags';
import { PipesModule } from '@freelancer/pipes';
import { TrackingModule } from '@freelancer/tracking';
import { UiModule } from '@freelancer/ui';
import { AiModalModule } from './ai-modal/ai-modal.module';
import { AutoCompleteModule } from './auto-complete/auto-complete.module';
import { AutoLinkDirective } from './auto-link/auto-link.directive';
import {
  BestPractisesComponent,
  DoBestPractisesComponent,
  DontBestPractisesComponent,
} from './best-practises';
import {
  BidPatternCardActionsComponent,
  BidPatternCardAnnotationComponent,
  BidPatternCardActionsNotesComponent,
  BidPatternCardAdditionalDetailsComponent,
  BidPatternCardComponent,
} from './bid-pattern-card/bid-pattern-card.component';
import { BouncingDotsComponent } from './bouncing-dots/bouncing-dots.component';
import { ChallengeStatusTagComponent } from './challenge-status-tag/challenge-status-tag.component';
import { ChatButtonComponent } from './chat-button/chat-button.component';
import { ChatButtonModule } from './chat-button/chat-button.module';
import { CompletedJobsComponent } from './completed-jobs';
import { ConfettiComponent } from './confetti';
import { CustomBannerComponent } from './custom-banner/custom-banner.component';
import { DynamicTextComponent } from './dynamic-text';
import { EmptyStateComponent } from './empty-state/empty-state.component';
import { ExperienceDetailsPatternComponent } from './experience/experience-details-pattern.component';
import { ExperienceComponent } from './experience/experience.component';
import { FileUploadComponent } from './file-upload/file-upload.component';
import { ImageCaptionComponent } from './image-caption';
import { InteractiveTextComponent } from './interactive-text/interactive-text.component';
import { LinkshimModule } from './linkshim/linkshim.module';
import { LoadingAvatarRowCardComponent } from './loading-avatar-row-card';
import { LoadingTextComponent } from './loading-text';
import { LoadingThreadItemComponent } from './loading-thread-item';
import { MyListsModule } from './my-lists/my-lists.module';
import {
  NewsfeedSystemMessagesBodyContentComponent,
  NewsfeedSystemMessagesBodyCopyComponent,
  NewsfeedSystemMessagesComponent,
  NewsfeedSystemMessagesFooterActionLeftComponent,
  NewsfeedSystemMessagesFooterActionMiddleComponent,
  NewsfeedSystemMessagesFooterActionRightComponent,
  NewsfeedSystemMessagesHeaderCopyEndComponent,
  NewsfeedSystemMessagesHeaderCopyStartComponent,
  NewsfeedSystemMessagesHedaderLinkComponent,
} from './newsfeed-system-messages';
import { ProjectContestCardTagsComponent } from './project-contest-card/project-contest-card-tags/project-contest-card-tags.component';
import { ProjectContestCardComponent } from './project-contest-card/project-contest-card.component';
import { RadioCardComponent } from './radio-card/radio-card.component';
import {
  ReviewCardComponent,
  ReviewCardReplyComponent,
  ReviewCardServicesComponent,
} from './review-card/review-card.component';
import {
  SectionArticleButtonComponent,
  SectionArticleComponent,
  SectionArticleDescriptionComponent,
  SectionArticleEntryComponent,
  SectionArticleEntryDetailComponent,
  SectionArticleEntryHeadingComponent,
  SectionArticleTitleComponent,
} from './section-article';
import {
  SectionCaseStudyCarouselComponent,
  SectionCaseStudyCarouselFooterComponent,
  SectionCaseStudyCarouselFooterHeaderComponent,
  SectionCaseStudyCarouselFooterSubHeaderComponent,
  SectionCaseStudyCarouselHeaderComponent,
  SectionCaseStudyCarouselHeaderDetailComponent,
  SectionCaseStudyCarouselItemComponent,
  SectionCaseStudyCarouselSubHeaderComponent,
} from './section-case-study-carousel';
import {
  SectionCompaniesComponent,
  SectionCompaniesTitleComponent,
} from './section-companies';
import {
  SectionFeatureBodyComponent,
  SectionFeatureButtonComponent,
  SectionFeatureButtonDetailComponent,
  SectionFeatureComponent,
  SectionFeatureDetailComponent,
  SectionFeatureHeadingComponent,
  SectionFeatureSubheadingComponent,
} from './section-feature';
import {
  SectionGalleryComponent,
  SectionGalleryItemComponent,
} from './section-gallery';
import {
  SectionHeroBackgroundImageComponent,
  SectionHeroComponent,
  SectionHeroCustomContentComponent,
  SectionHeroDescriptionComponent,
  SectionHeroDetailTextComponent,
  SectionHeroImageDetailComponent,
  SectionHeroLogoComponent,
  SectionHeroTitleComponent,
  SectionHeroVideoBackgroundComponent,
} from './section-hero';
import {
  ServiceOfferingCarouselCardComponent,
  ServiceOfferingCarouselComponent,
  ServiceOfferingCarouselHeaderComponent,
  ServiceOfferingCarouselHeaderDisplayComponent,
  ServiceOfferingCarouselHeaderEditComponent,
} from './service-offering-carousel';
import {
  ShowcaseCardHoverActionComponent,
  ShowcaseCardHoverContentComponent,
} from './showcase-card/showcase-card-hover-content.component';
import { ShowcaseCardPriceComponent } from './showcase-card/showcase-card-price.component';
import {
  ShowcaseCardCarouselItemComponent,
  ShowcaseCardComponent,
  ShowcaseCardDescriptionComponent,
  ShowcaseCardExpandableContentComponent,
  ShowcaseCardTitleComponent,
} from './showcase-card/showcase-card.component';
import { ShowcaseLikesComponent } from './showcase-card/showcase-likes.component';
import { ShowcaseThumbnailComponent } from './showcase-thumbnail/showcase-thumbnail.component';
import { SkillsModule } from './skills/skills.module';
import { TasklistsModule } from './tasklists/tasklists.module';
import { UpgradeItemComponent } from './upgrade-item/upgrade-item.component';
import { UserFeedbackModule } from './user-feedback/user-feedback.module';
import {
  UserInfoComponent,
  UserInfoMyListComponent,
} from './user-info/user-info.component';
import { UserRehireCardComponent } from './user-rehire-card';
import {
  UserSearchCardActionComponent,
  UserSearchCardComponent,
} from './user-search-card/user-search-card.component';
import { ProjectBookmarkButtonModule } from '../../app/projects/project-view/project-view-details/project-bookmark-button/project-bookmark-button.module';
import { DirectQuoteButtonModule } from '../../app/quotations/direct-quote-button/direct-quote-button.module';

@NgModule({
  imports: [
    AiModalModule,
    AutoCompleteModule,
    AutoLinkDirective,
    BouncingDotsComponent,
    ChallengeStatusTagComponent,
    ChatButtonModule,
    CommonModule,
    ConfettiComponent,
    DirectQuoteButtonModule,
    DynamicTextComponent,
    EmptyStateComponent,
    FeatureFlagsModule,
    LinkshimModule,
    LoadingAvatarRowCardComponent,
    LoadingTextComponent,
    LoadingThreadItemComponent,
    MatTooltipModule,
    MyListsModule,
    PipesModule,
    ProjectBookmarkButtonModule,
    SkillsModule,
    TasklistsModule,
    TrackingModule,
    UiModule,
    UserFeedbackModule,
  ],
  declarations: [
    AcceptRateComponent,
    BestPractisesComponent,
    BidPatternCardActionsComponent,
    BidPatternCardAnnotationComponent,
    BidPatternCardActionsNotesComponent,
    BidPatternCardAdditionalDetailsComponent,
    BidPatternCardComponent,
    CompletedJobsComponent,
    CustomBannerComponent,
    DoBestPractisesComponent,
    DontBestPractisesComponent,
    EarningsComponent,
    DirectoryItemComponent,
    ExperienceComponent,
    ExperienceDetailsPatternComponent,
    FileUploadComponent,
    ImageCaptionComponent,
    InteractiveTextComponent,
    NewsfeedSystemMessagesBodyContentComponent,
    NewsfeedSystemMessagesBodyCopyComponent,
    NewsfeedSystemMessagesComponent,
    NewsfeedSystemMessagesFooterActionLeftComponent,
    NewsfeedSystemMessagesFooterActionMiddleComponent,
    NewsfeedSystemMessagesFooterActionRightComponent,
    NewsfeedSystemMessagesHeaderCopyEndComponent,
    NewsfeedSystemMessagesHeaderCopyStartComponent,
    NewsfeedSystemMessagesHedaderLinkComponent,
    ProjectContestCardComponent,
    ProjectContestCardTagsComponent,
    RadioCardComponent,
    ReviewCardComponent,
    ReviewCardReplyComponent,
    ReviewCardServicesComponent,
    SectionArticleButtonComponent,
    SectionArticleComponent,
    SectionArticleDescriptionComponent,
    SectionArticleEntryComponent,
    SectionArticleEntryDetailComponent,
    SectionArticleEntryHeadingComponent,
    SectionArticleTitleComponent,
    SectionCaseStudyCarouselComponent,
    SectionCaseStudyCarouselFooterComponent,
    SectionCaseStudyCarouselFooterHeaderComponent,
    SectionCaseStudyCarouselFooterSubHeaderComponent,
    SectionCaseStudyCarouselHeaderComponent,
    SectionCaseStudyCarouselHeaderDetailComponent,
    SectionCaseStudyCarouselItemComponent,
    SectionCaseStudyCarouselSubHeaderComponent,
    SectionCompaniesComponent,
    SectionCompaniesTitleComponent,
    SectionFeatureBodyComponent,
    SectionFeatureButtonComponent,
    SectionFeatureButtonDetailComponent,
    SectionFeatureComponent,
    SectionFeatureDetailComponent,
    SectionFeatureHeadingComponent,
    SectionFeatureSubheadingComponent,
    SectionGalleryComponent,
    SectionGalleryItemComponent,
    SectionHeroBackgroundImageComponent,
    SectionHeroComponent,
    SectionHeroCustomContentComponent,
    SectionHeroDescriptionComponent,
    SectionHeroDetailTextComponent,
    SectionHeroImageDetailComponent,
    SectionHeroLogoComponent,
    SectionHeroTitleComponent,
    SectionHeroVideoBackgroundComponent,
    ReviewCountComponent,
    ServiceOfferingCarouselCardComponent,
    ServiceOfferingCarouselComponent,
    ServiceOfferingCarouselHeaderComponent,
    ServiceOfferingCarouselHeaderDisplayComponent,
    ServiceOfferingCarouselHeaderEditComponent,
    ShowcaseCardCarouselItemComponent,
    ShowcaseCardComponent,
    ShowcaseCardDescriptionComponent,
    ShowcaseCardExpandableContentComponent,
    ShowcaseCardHoverActionComponent,
    ShowcaseCardHoverContentComponent,
    ShowcaseCardPriceComponent,
    ShowcaseCardTitleComponent,
    ShowcaseLikesComponent,
    ShowcaseThumbnailComponent,
    UpgradeItemComponent,
    UpgradeTagComponent,
    UserInfoComponent,
    UserInfoMyListComponent,
    UsernameComponent,
    UserRehireCardComponent,
    UserSearchCardActionComponent,
    UserSearchCardComponent,
    VideoRecordInputComponent,
    WorkedTimeComponent,
  ],
  exports: [
    AcceptRateComponent,
    AiModalModule,
    AutoCompleteModule,
    AutoLinkDirective,
    BestPractisesComponent,
    BidPatternCardActionsComponent,
    BidPatternCardAnnotationComponent,
    BidPatternCardActionsNotesComponent,
    BidPatternCardAdditionalDetailsComponent,
    BidPatternCardComponent,
    BouncingDotsComponent,
    ChallengeStatusTagComponent,
    CompletedJobsComponent,
    ConfettiComponent,
    ChatButtonComponent,
    CustomBannerComponent,
    DirectoryItemComponent,
    DoBestPractisesComponent,
    DontBestPractisesComponent,
    DynamicTextComponent,
    EarningsComponent,
    EmptyStateComponent,
    ExperienceComponent,
    ExperienceDetailsPatternComponent,
    FileUploadComponent,
    ImageCaptionComponent,
    InteractiveTextComponent,
    LinkshimModule,
    LoadingAvatarRowCardComponent,
    LoadingTextComponent,
    LoadingThreadItemComponent,
    MyListsModule,
    NewsfeedSystemMessagesBodyContentComponent,
    NewsfeedSystemMessagesBodyCopyComponent,
    NewsfeedSystemMessagesComponent,
    NewsfeedSystemMessagesFooterActionLeftComponent,
    NewsfeedSystemMessagesFooterActionMiddleComponent,
    NewsfeedSystemMessagesFooterActionRightComponent,
    NewsfeedSystemMessagesHeaderCopyEndComponent,
    NewsfeedSystemMessagesHeaderCopyStartComponent,
    NewsfeedSystemMessagesHedaderLinkComponent,
    ProjectContestCardComponent,
    RadioCardComponent,
    ReviewCardComponent,
    ReviewCardReplyComponent,
    ReviewCardServicesComponent,
    SectionArticleButtonComponent,
    SectionArticleComponent,
    SectionArticleDescriptionComponent,
    SectionArticleEntryComponent,
    SectionArticleEntryDetailComponent,
    SectionArticleEntryHeadingComponent,
    SectionArticleTitleComponent,
    SectionCaseStudyCarouselComponent,
    SectionCaseStudyCarouselFooterComponent,
    SectionCaseStudyCarouselFooterHeaderComponent,
    SectionCaseStudyCarouselFooterSubHeaderComponent,
    SectionCaseStudyCarouselHeaderComponent,
    SectionCaseStudyCarouselHeaderDetailComponent,
    SectionCaseStudyCarouselItemComponent,
    SectionCaseStudyCarouselSubHeaderComponent,
    SectionCompaniesComponent,
    SectionCompaniesTitleComponent,
    SectionFeatureBodyComponent,
    SectionFeatureButtonComponent,
    SectionFeatureButtonDetailComponent,
    SectionFeatureComponent,
    SectionFeatureDetailComponent,
    SectionFeatureHeadingComponent,
    SectionFeatureSubheadingComponent,
    SectionGalleryComponent,
    SectionGalleryItemComponent,
    SectionHeroBackgroundImageComponent,
    SectionHeroComponent,
    SectionHeroCustomContentComponent,
    SectionHeroDescriptionComponent,
    SectionHeroDetailTextComponent,
    SectionHeroImageDetailComponent,
    SectionHeroLogoComponent,
    SectionHeroTitleComponent,
    SectionHeroVideoBackgroundComponent,
    ReviewCountComponent,
    ServiceOfferingCarouselCardComponent,
    ServiceOfferingCarouselComponent,
    ServiceOfferingCarouselHeaderComponent,
    ShowcaseCardCarouselItemComponent,
    ShowcaseCardComponent,
    ShowcaseCardDescriptionComponent,
    ShowcaseCardExpandableContentComponent,
    ShowcaseCardHoverActionComponent,
    ShowcaseCardHoverContentComponent,
    ShowcaseCardPriceComponent,
    ShowcaseCardTitleComponent,
    ShowcaseLikesComponent,
    ShowcaseThumbnailComponent,
    SkillsModule,
    TasklistsModule,
    UpgradeItemComponent,
    UpgradeTagComponent,
    UserInfoComponent,
    UserInfoMyListComponent,
    UsernameComponent,
    UserRehireCardComponent,
    UserSearchCardActionComponent,
    UserSearchCardComponent,
    VideoRecordInputComponent,
    WorkedTimeComponent,
  ],
})
export class ComponentsModule {}
